<template>
  <div
    id="exercises-view"
    class="pa-4"
  >
    <section class="exercises-view__exercises mb-6">
      <v-card class="rounded-lg">
        <v-card-text>
          <div class="col col-auto pa-0">
            <v-row
              no-gutters
              align="center"
            >
              <v-spacer />
              <v-col cols="auto">
                <v-btn
                  elevation="0"
                  :color="filters.groupId === 'groups' ? 'primary' : 'grey lighten-4'"
                  class="text-none mr-4"
                  dense
                  @click="handleGroupFilter('groups')"
                >
                  {{ $t('training_builder.groups.saved') }}
                </v-btn>
                <v-btn
                  v-for="group in filteredGroups"
                  :key="'group'+group.id"
                  class="text-none mr-2"
                  elevation="0"
                  :color="filters.groupId === group.id ? 'primary' : 'grey lighten-4'"
                  dense
                  @click="handleGroupFilter(group.id)"
                >
                  {{ group.name }}
                </v-btn>
              </v-col>
            </v-row>
            <v-row
              class="py-4"
              no-gutters
              style="min-height: 72px"
              align="center"
            >
              <v-col
                class="mr-2"
              >
                <v-text-field
                  v-model="filters.search"
                  dense
                  outlined
                  hide-details
                  append-icon="fal fa-search"
                  :placeholder="$t('training_builder.text_input.search_exercises')"
                  style="max-width: 400px"
                  clearable
                />
              </v-col>

              <template v-if="selectedGroup && selectedGroup.params.tags">
                <v-col
                  v-for="tag in (selectedGroup.params.tags.filter(t => !t.is_hidden) || [])"
                  :key="'filter'+tag.id"
                  cols="auto"
                  class="mr-2"
                >
                  <v-select
                    :value="filters.tags[tag.id]"
                    :items="tag.values"
                    :label="tag.label"
                    dense
                    outlined
                    style="width: 140px"
                    clearable
                    hide-details
                    @input="handleFilterTag(tag.id, $event)"
                  />
                </v-col>
              </template>
            </v-row>
          </div>
          <v-divider />
          <div style="height: 1px">
            <v-progress-linear
              v-if="exercises.loading || userGroups.loading"
              indeterminate
              color="primary"
              :height="1"
            />
          </div>
          <div>
            <v-row
              no-gutters
              align="center"
              style="min-height: 48px"
            >
              <v-col>
                <v-switch
                  v-model="filters.onlyCustom"
                  dense
                  :label="$t('training_builder.switch.only_custom_exercises')"
                  class="pa-0 ma-0"
                  hide-details
                />
              </v-col>
              <v-col
                cols="auto"
                class="pt-1"
              >
                <v-btn
                  color="primary"
                  text
                  class="text-none"
                  @click="createNewExercise()"
                >
                  <v-icon
                    x-small
                    left
                  >
                    far fa-plus
                  </v-icon>
                  {{ $t("training_builder.button.create_new_exercise") }}
                </v-btn>
              </v-col>
              <v-col cols="auto">
                <div
                  v-if="filters.groupId !== 'groups' && exercises.pagination.last_page > 1"
                  class="pt-1"
                >
                  <pagination
                    v-model="exercises.pagination.current_page"
                    :last-page="exercises.pagination.last_page"
                  />
                </div>
                <div
                  v-else-if="filters.groupId === 'groups' && userGroups.pagination.last_page > 1"
                  class="pt-1"
                >
                  <pagination
                    v-model="userGroups.pagination.current_page"
                    :last-page="userGroups.pagination.last_page"
                  />
                </div>
              </v-col>
            </v-row>
          </div>

          <div
            v-if="filters.groupId !== 'groups'"
            class="pa-3 mt-2"
          >
            <div
              v-if="exercises.data.length === 0 && !exercises.loading"
              class="py-12 grey--text text-center"
            >
              {{ $t("training_builder.no_exercises.label") }}
            </div>
            <v-row v-else>
              <exercise-card
                v-for="exercise in exercises.data"
                :key="'library-exercise-'+exercise.id"
                :exercise="exercise"
                :allow-add-to-protocol="false"
                @click="onEditExercise(exercise)"
              />
            </v-row>
          </div>
          <div
            v-else
            class="pa-3 mt-2"
          >
            <div
              v-if="userGroups.data.length === 0 && !userGroups.data.loading"
              class="py-12 grey--text text-center"
            >
              {{ filters.search === '' ? $t('training_builder.no_save_sections.label') : $t('training_builder.no_save_sections_match_filter.label') }}
            </div>
            <v-row>
              <user-exercise-group-card
                v-for="userGroup in userGroups.data"
                :key="'user-group-'+userGroup.id"
                :group="userGroup"
                :allow-add-to-protocol="false"
              />
            </v-row>
          </div>
        </v-card-text>
      </v-card>
    </section>
  </div>
</template>

<script>
import debounce from 'lodash/debounce'
import { reactive } from '@vue/composition-api'
import { EventBus } from '@/plugins/bus'
import Pagination from '@/components/Pagination.vue'
import { TrainingExerciseApiService } from '@/data/services/TrainingExerciseApiService'
import ExerciseCard from '@/components/training-builder/ExerciseCard.vue'
import UserExerciseGroupCard from '@/components/training-builder/UserExerciseGroupCard.vue'
import i18n from '@/plugins/i18n'

export default {
  name: 'TrainingExercisesView',
  components: {
    UserExerciseGroupCard,
    ExerciseCard,
    Pagination
  },
  data: () => ({
    userGroups: {
      loading: false,
      data: [],
      pagination: {
        current_page: 1,
        last_page: undefined,
        per_page: 30
      }
    },
    filters: {
      groupId: undefined,
      tags: {},
      search: '',
      onlyCustom: false
    },
    sportSpecificGroups: [2]
  }),
  setup(props) {
    const tableHeaders = [
      { value: 'name', text: i18n.t('table_headers.name'), sortable: false },
      { value: 'group_id', text: i18n.t('table_headers.new_diagnostic_protocol.group'), sortable: false },
      { text: '', sortable: false }
    ]

    const exercises = reactive({
      data: [],
      loading: true,
      pagination: {
        current_page: 1,
        last_page: undefined,
        per_page: 25
      },
      filters: {
        search: null,
        groupId: null
      }
    })

    const groups = reactive({
      data: [],
      loading: false
    })

    const fetchGroups = async function() {
      groups.loading = true

      try {
        groups.data = await TrainingExerciseApiService.getAllGroups()
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_groups.error'),
          color: 'error'
        })

        throw e
      } finally {
        groups.loading = false
      }
    }

    fetchGroups()

    const fetchExercises = debounce(async function () {
      if (this.filters.groupId === 'groups') return

      try {
        exercises.loading = true

        const queryParams = {
          page: exercises.pagination.current_page,
          per_page: exercises.pagination.per_page,
          search: this.filters.search,
          group_id: this.filters.groupId,
          tags: this.filters.tags,
          only_user_exercises: this.filters.onlyCustom || undefined
        }

        const resp = await TrainingExerciseApiService.get(queryParams)

        exercises.data = resp.data
        exercises.pagination = resp.meta
      } catch (e) {
        exercises.data = []
        exercises.pagination.current_page = 1
        exercises.pagination.last_page = 1
      } finally {
        exercises.loading = false
      }
    }, 500)

    return {
      exercises,
      groups,
      fetchExercises,
      tableHeaders
    }
  },
  computed: {
    selectedGroup() {
      return this.groups.data.find(g => g.id === this.filters.groupId)
    },
    filteredGroups() {
      if (this.$route.query.view === 'sport-specific') {
        return this.groups.data.filter(group => this.sportSpecificGroups.includes(group.id))
      } else {
        return this.groups.data.filter(group => !this.sportSpecificGroups.includes(group.id))
      }
    }
  },
  watch: {
    'exercises.pagination.current_page': function (newVal, oldVal) {
      this.fetchExercises()
    },
    'filters.search': function () {
      if (this.filters.groupId === 'groups') {
        if (this.userGroups.pagination.current_page === 1) {
          this.fetchUserGroups()
        } else {
          this.userGroups.pagination.current_page = 1
        }
      } else {
        if (this.exercises.pagination.current_page === 1) {
          this.fetchExercises()
        } else {
          this.exercises.pagination.current_page = 1
        }
      }
    },
    'filters.groupId': function (val, oldVal) {
      if (val === 'groups') {
        if (this.userGroups.pagination.current_page !== 1) {
          this.userGroups.pagination.current_page = 1
        } else {
          this.fetchUserGroups()
        }
      } else {
        if (this.exercises.pagination.current_page === 1) {
          this.fetchExercises()
        } else {
          this.exercises.pagination.current_page = 1
        }
      }
    },
    'filters.onlyCustom': function () {
      if (this.exercises.pagination.current_page === 1) {
        this.fetchExercises()
      } else {
        this.exercises.pagination.current_page = 1
      }
    },
    'userGroups.pagination.current_page': function () {
      this.fetchUserGroups()
    },
    '$route.query.view': function (val) {
      if (val === 'sport-specific') {
        this.filters.groupId = this.sportSpecificGroups[0]
      } else {
        this.filters.groupId = undefined
      }
    }
  },
  created() {
    if (this.$route.query.view === 'sport-specific') this.filters.groupId = this.sportSpecificGroups[0]

    this.fetchExercises()

    EventBus.$on(this.$events.TRAINING_EXERCISE_CREATED, () => this.fetchExercises())
    EventBus.$on(this.$events.TRAINING_EXERCISE_UPDATED, () => this.fetchExercises())
    EventBus.$on(this.$events.TRAINING_EXERCISE_DELETED, () => {
      if (this.exercises.pagination.current_page !== 1) {
        this.exercises.pagination.current_page = 1
      } else {
        this.fetchExercises()
      }
    })
  },
  beforeDestroy() {
    EventBus.$off(this.$events.TRAINING_EXERCISE_CREATED)
    EventBus.$off(this.$events.TRAINING_EXERCISE_UPDATED)
    EventBus.$off(this.$events.TRAINING_EXERCISE_DELETED)
  },
  methods: {
    fetchUserGroups: debounce(async function () {
      if (this.filters.groupId !== 'groups') return

      if (this.userGroups.loading) return

      this.userGroups.loading = true

      try {
        const result = await TrainingExerciseApiService.getUserGroups({
          search: this.filters.search,
          page: this.userGroups.pagination.current_page,
          per_page: this.userGroups.pagination.per_page
        })

        this.userGroups.data = result.data
        this.userGroups.pagination = result.meta
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_saved_exercise_groups.error'),
          color: 'error'
        })

        throw e
      } finally {
        this.userGroups.loading = false
      }
    }, 300),
    onEditExercise(exercise) {
      if (exercise.organisation_id) EventBus.$emit(this.$events.EDIT_TRAINING_EXERCISE, exercise)
    },
    handleGroupFilter(groupId) {
      if (groupId === this.filters.groupId) return

      this.filters.tags = {}
      this.filters.groupId = groupId
    },
    handleFilterTag(tagId, tagValue) {
      if (!this.filters.tags[tagId] && !tagValue) return

      this.filters.tags[tagId] = !tagValue ? undefined : tagValue

      if (this.exercises.pagination.current_page !== 1) {
        this.exercises.pagination.current_page = 1
      } else {
        this.fetchExercises()
      }
    },
    createNewExercise() {
      EventBus.$emit(this.$events.OPEN_DIALOG_TRAINING_EXERCISE_FORM)
    }
  }
}
</script>
